/**
 * @file Calculating costs and billing for Digiknow ads.
 */
import {BillingPlan, DigiknowOrgType} from './lib/types';
import * as R from 'ramda';

export type BillableAd = {
  organizationType: DigiknowOrgType,
  slideCount: number,
  paidAt?: string|Date
};

/**
 * Either an ad, or an ad collection with an associated
 * cost that can potentially be invoiced.
 */
export type Invoiceable = {
  organizationType: DigiknowOrgType,
  /**
   * The total number of cost units for the ad(s).
   * This could be a single ad with one or more slides,
   * which get charged per slide.
   * It could also be the total number of slides from multiple ads,
   * each with one or more slides.
   */
  slideCount: number,
  billingPlan: BillingPlan
}

export const costPerSlide = {
  'Campus Department': 25,
  'Campus Department w/ Sponsorship Logo': 40,
  'Housing/Residential Dining': 0,
  'Registered Student Group': 10
}

/**
 * How much it costs to display a single ad.
 * Not applicable to quarterly payment plans
 */
export function adCostDollars(ad: BillableAd): number {
  const slideCost: (_: BillableAd) => number =
    R.pipe(
      R.prop('organizationType') as (_: BillableAd) => DigiknowOrgType,
      orgType => costPerSlide[orgType]
    );

  return slideCost(ad) * ad.slideCount;
}

export function adInvoiceCostDollars(ad: Invoiceable): number {
  const {billingPlan, organizationType} = ad;
  if (organizationType === 'Campus Department' && billingPlan === 'quarterly') {
    return 100;
  } else if (organizationType === 'Campus Department' && billingPlan === 'per-ad') {
    return adCostDollars(ad);
  } else if (organizationType === 'Campus Department w/ Sponsorship Logo' && billingPlan === 'quarterly') {
    return 160;
  } else if (organizationType === 'Campus Department w/ Sponsorship Logo' && billingPlan === 'per-ad') {
    return adCostDollars(ad);
  } else if (organizationType === 'Registered Student Group' && billingPlan === 'quarterly') {
    return 40;
  } else if (organizationType === 'Registered Student Group' && billingPlan === 'per-ad') {
    return adCostDollars(ad);
  } else {
    return 0;
  }
}

/**
 * Display the cost of an @see Invoiceable, whether it be a single payment
 * for running a single ad, or included within a quarterly
 * payment plan.
 */
export function displayAdCost(ad: Invoiceable): string {
  const cost = adInvoiceCostDollars(ad);
  return ad.billingPlan === 'quarterly'
    ? `$${cost}/quarter`
    : `$${cost}`;
}

function trimTrailingZeroes(str: string): string {
  while (str.charAt(str.length - 1) === '0') {
    str = str.substr(0, str.length - 1);
  }
  return str;
}

export const invoiceNumber: (_: {orderedAt: string}) => string =
  R.pipe(
    R.prop('orderedAt'),
    R.invoker(2, 'replace')(/\D/g, ''),
    trimTrailingZeroes,
    R.invoker(1, 'substr')(2)
  );
